import React, { useState, useEffect, useMemo } from 'react';
import {
  Droplets,
  Clock,
  Sparkles,
  RotateCcw,
  CheckCircle2,
  Calendar,
  Timer,
  Zap,
} from 'lucide-react';
import { CoupleSettings, GardenWateringState } from '../../types';
import { calculateHydrationState, playWateringSound, getEffectiveCanWaterLevel, WATER_PER_POUR } from '../../utils/wateringUtils';
import { recordWateringInDb } from '../../services/firebaseDb';

interface WateringAdminSectionProps {
  settings: CoupleSettings;
  onSaveSettings: (newSettings: CoupleSettings) => void;
  showCardWrapper?: boolean;
}

export const WateringAdminSection: React.FC<WateringAdminSectionProps> = ({
  settings,
  onSaveSettings,
  showCardWrapper = true,
}) => {
  const [isWatering, setIsWatering] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Real-time ticking every second for live countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const wateringConfig: GardenWateringState = useMemo(() => {
    return (
      settings.gardenWatering || {
        lastWateredAt: new Date().toISOString(),
        wateringIntervalDays: 3,
        wateredCount: 0,
        witheringEnabled: true,
      }
    );
  }, [settings.gardenWatering]);

  const hydration = useMemo(() => {
    return calculateHydrationState(
      wateringConfig.lastWateredAt,
      wateringConfig.wateringIntervalDays || 3
    );
  }, [wateringConfig.lastWateredAt, wateringConfig.wateringIntervalDays]);

  // Evaluates 24-hour cycle live with each second tick
  const canWaterStatus = useMemo(() => {
    return getEffectiveCanWaterLevel(wateringConfig, nowMs);
  }, [wateringConfig, nowMs]);

  // Auto-sync back to Firestore if 24 hours elapsed and can should be 100%
  useEffect(() => {
    if (canWaterStatus.needsDailyReset) {
      const updated: GardenWateringState = {
        ...wateringConfig,
        canWaterLevel: 100,
        lastCanUsedAt: null as any,
        lastWaterRefillDate: canWaterStatus.todayStr,
      };
      recordWateringInDb(updated).catch((err) => {
        console.error('Error auto-syncing 24h refill:', err);
      });
      onSaveSettings({
        ...settings,
        gardenWatering: updated,
      });
    }
  }, [canWaterStatus.needsDailyReset, canWaterStatus.todayStr]);

  const handleManualWater = async () => {
    if (isWatering) return;
    if (canWaterStatus.isEmpty) {
      setSuccessMsg('⚠️ ¡La regadera está vacía (0%)! Espera a que termine el contador de 24h o recárgala ahora.');
      setTimeout(() => setSuccessMsg(null), 4000);
      return;
    }

    setIsWatering(true);
    playWateringSound();

    try {
      const nowIso = new Date().toISOString();
      const prevUsedAt = wateringConfig.lastCanUsedAt;
      const isPrevCycleActive = prevUsedAt && (Date.now() - Date.parse(prevUsedAt) < 24 * 60 * 60 * 1000);
      const startCountdownAt = isPrevCycleActive ? prevUsedAt : nowIso;

      const updated: GardenWateringState = {
        ...wateringConfig,
        lastWateredAt: nowIso,
        wateredCount: (wateringConfig.wateredCount || 0) + 1,
        canWaterLevel: Math.max(0, canWaterStatus.canWaterLevel - WATER_PER_POUR),
        lastWaterRefillDate: canWaterStatus.todayStr,
        lastCanUsedAt: startCountdownAt,
        flowerWateredAt: [nowIso, nowIso, nowIso, nowIso],
      };

      await recordWateringInDb(updated);
      onSaveSettings({
        ...settings,
        gardenWatering: updated,
      });

      setSuccessMsg('¡Flores regadas con amor! 💧 El contador de 24h para recarga está en marcha.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error in handleManualWater:', err);
    } finally {
      setIsWatering(false);
    }
  };

  const handleSetCanWater = async (level: number) => {
    try {
      const nowIso = new Date().toISOString();
      const isRefillingFull = level >= 100;
      const updated: GardenWateringState = {
        ...wateringConfig,
        canWaterLevel: level,
        lastWaterRefillDate: canWaterStatus.todayStr,
        lastCanUsedAt: isRefillingFull ? (null as any) : (wateringConfig.lastCanUsedAt || nowIso),
      };
      await recordWateringInDb(updated);
      onSaveSettings({
        ...settings,
        gardenWatering: updated,
      });
      setSuccessMsg(
        isRefillingFull
          ? '¡Regadera recargada al 100% de inmediato!'
          : `Nivel de la regadera ajustado a ${level}%. Contador de 24h iniciado.`
      );
      setTimeout(() => setSuccessMsg(null), 3500);
    } catch (err) {
      console.error('Error setting can water level:', err);
    }
  };

  const stageBadge = () => {
    switch (hydration.stage) {
      case 'thriving':
        return {
          label: '🌿 100% Lozanas y Vivas',
          className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        };
      case 'thirsty':
        return {
          label: '💧 Sedientas (Pérdida de turgencia)',
          className: 'bg-sky-50 text-sky-700 border-sky-200',
        };
      case 'wilting':
        return {
          label: '🍂 Marchitándose (Inclinadas)',
          className: 'bg-amber-50 text-amber-700 border-amber-200',
        };
      case 'withered':
      default:
        return {
          label: '🥀 Marchitas (Sequía de 3+ días)',
          className: 'bg-rose-50 text-rose-700 border-rose-200',
        };
    }
  };

  const badge = stageBadge();

  const content = (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-600">
            <Droplets className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
              <span>Sistema de Riego de la Regadera</span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                Ciclo de 24 Horas
              </span>
            </h3>
            <p className="text-xs text-slate-500">
              La regadera inicia un contador de 24 horas al usarse para volver a llenarse al 100% automáticamente.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleManualWater}
          disabled={isWatering}
          className="px-4 py-2 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-bold text-xs rounded-xl shadow-xs transition-all hover:scale-102 active:scale-98 flex items-center gap-2 cursor-pointer disabled:opacity-50"
        >
          <Droplets className={`w-4 h-4 ${isWatering ? 'animate-bounce' : ''}`} />
          <span>{isWatering ? 'Regando...' : 'Regar con Regadera'}</span>
        </button>
      </div>

      {/* STATUS DASHBOARD */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Card 1: Hydration Level */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            Salud de las Flores
          </span>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-slate-900 font-mono">
              {hydration.hydrationPercent}%
            </span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          {/* Visual Progress Bar */}
          <div className="w-full h-2 rounded-full bg-slate-200 mt-3 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 rounded-full ${
                hydration.hydrationPercent > 60
                  ? 'bg-gradient-to-r from-emerald-500 to-sky-400'
                  : hydration.hydrationPercent > 30
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-400'
                  : 'bg-gradient-to-r from-rose-500 to-red-600'
              }`}
              style={{ width: `${hydration.hydrationPercent}%` }}
            />
          </div>
        </div>

        {/* Card 2: Watering Can Capacity & Resource */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
            <Droplets className="w-3.5 h-3.5 text-sky-600" />
            Agua en la Regadera
          </span>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-sky-700 font-mono">
              {canWaterStatus.canWaterLevel}%
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
              canWaterStatus.isEmpty
                ? 'bg-rose-50 text-rose-700 border-rose-200'
                : canWaterStatus.isCountingDown
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-emerald-50 text-emerald-700 border-emerald-200'
            }`}>
              {canWaterStatus.isEmpty ? 'Vacía' : canWaterStatus.isCountingDown ? 'En recarga' : 'Disponible'}
            </span>
          </div>
          {/* Visual Water Level */}
          <div className="w-full h-2 rounded-full bg-slate-200 mt-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-sky-500 transition-all duration-500 rounded-full"
              style={{ width: `${canWaterStatus.canWaterLevel}%` }}
            />
          </div>
          {/* Quick test buttons */}
          <div className="flex items-center gap-1 mt-2.5">
            <button
              type="button"
              onClick={() => handleSetCanWater(100)}
              className="px-2 py-0.5 text-[10px] font-semibold bg-sky-50 hover:bg-sky-100 border border-sky-300 text-sky-700 rounded-md transition-all cursor-pointer"
            >
              100% (Llenar)
            </button>
            <button
              type="button"
              onClick={() => handleSetCanWater(0)}
              className="px-2 py-0.5 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded-md transition-all cursor-pointer"
            >
              0% (Vaciar)
            </button>
          </div>
        </div>

        {/* Card 3: Last Watered */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
            <Clock className="w-3.5 h-3.5 text-amber-500" />
            Último Riego
          </span>
          <div className="mt-2">
            <span className="text-sm font-semibold text-slate-900">
              {hydration.timeSinceText}
            </span>
            <p className="text-[11px] text-slate-500 mt-1">
              {new Date(wateringConfig.lastWateredAt).toLocaleString('es-ES', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          </div>
        </div>

        {/* Card 4: Watered Count */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
            <Calendar className="w-3.5 h-3.5 text-purple-600" />
            Veces Regado
          </span>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-slate-900 font-mono">
              {wateringConfig.wateredCount || 0}
            </span>
            <span className="text-xs text-slate-500">veces con amor</span>
          </div>
          <p className="text-[10px] text-sky-600 font-medium mt-1">
            Recarga en 24h tras su uso
          </p>
        </div>
      </div>

      {/* 24-HOUR REFILL COUNTDOWN PANEL */}
      <div className="p-5 rounded-2xl bg-gradient-to-b from-sky-50/60 via-slate-50 to-white border border-sky-200 space-y-4 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-sky-100 border border-sky-300 flex items-center justify-center text-sky-700">
              <Timer className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                <span>Contador de Recarga de la Regadera (24 Horas)</span>
              </h4>
              <p className="text-xs text-slate-500">
                El tiempo comienza en el instante en que se use la regadera para que se vuelva a llenar automáticamente.
              </p>
            </div>
          </div>

          <span
            className={`text-xs font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${
              canWaterStatus.isCountingDown
                ? 'bg-amber-50 text-amber-700 border-amber-300 animate-pulse'
                : 'bg-emerald-50 text-emerald-700 border-emerald-300'
            }`}
          >
            {canWaterStatus.isCountingDown ? (
              <>
                <Clock className="w-3.5 h-3.5 text-amber-600" />
                <span>Cuenta Regresiva Activa</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                <span>100% Llena y Lista</span>
              </>
            )}
          </span>
        </div>

        {canWaterStatus.isCountingDown ? (
          <div className="space-y-4">
            {/* Live Ticking Countdown Clock Display */}
            <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto text-center pt-1">
              <div className="p-3.5 rounded-xl bg-white border border-sky-200 shadow-xs">
                <div className="text-3xl sm:text-4xl font-mono font-black text-sky-700 tracking-wider">
                  {String(canWaterStatus.hoursLeft).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">
                  Horas
                </div>
              </div>
              <div className="p-3.5 rounded-xl bg-white border border-sky-200 shadow-xs">
                <div className="text-3xl sm:text-4xl font-mono font-black text-sky-700 tracking-wider">
                  {String(canWaterStatus.minutesLeft).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">
                  Minutos
                </div>
              </div>
              <div className="p-3.5 rounded-xl bg-white border border-sky-200 shadow-xs">
                <div className="text-3xl sm:text-4xl font-mono font-black text-amber-600 tracking-wider">
                  {String(canWaterStatus.secondsLeft).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">
                  Segundos
                </div>
              </div>
            </div>

            {/* Time Description */}
            <p className="text-center text-xs text-slate-700 font-medium">
              Faltan <strong className="text-sky-700 font-mono">{canWaterStatus.countdownTextSpanish}</strong> para que la regadera se llene completamente al 100%.
            </p>

            {/* Visual 24h Progress Bar */}
            <div className="space-y-1.5 px-1">
              <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                <span>Progreso hacia la recarga completa (24 horas)</span>
                <span className="font-mono text-sky-700 font-bold">{canWaterStatus.progressPercent.toFixed(1)}%</span>
              </div>
              <div className="w-full h-3 rounded-full bg-slate-200 overflow-hidden p-0.5 border border-slate-300">
                <div
                  className="h-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 transition-all duration-500 rounded-full"
                  style={{ width: `${canWaterStatus.progressPercent}%` }}
                />
              </div>
            </div>

            {/* Timestamps details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-200 text-slate-600">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200">
                <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                <div className="truncate">
                  <span className="text-slate-500">Inicio del contador: </span>
                  <strong className="text-slate-900">
                    {canWaterStatus.usedAt
                      ? new Date(canWaterStatus.usedAt).toLocaleString('es-ES', {
                          dateStyle: 'medium',
                          timeStyle: 'medium',
                        })
                      : 'Al usar la regadera'}
                  </strong>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200">
                <Droplets className="w-4 h-4 text-teal-600 shrink-0" />
                <div className="truncate">
                  <span className="text-slate-500">Recarga completa (100%): </span>
                  <strong className="text-slate-900">
                    {canWaterStatus.refillAt
                      ? new Date(canWaterStatus.refillAt).toLocaleString('es-ES', {
                          dateStyle: 'medium',
                          timeStyle: 'medium',
                        })
                      : 'En 24 horas'}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-300 flex items-center justify-center text-emerald-700 shrink-0">
              <Droplets className="w-6 h-6 animate-pulse" />
            </div>
            <div className="space-y-1 flex-1">
              <h5 className="text-sm font-bold text-slate-900 flex items-center justify-center sm:justify-start gap-1.5">
                <span>Regadera al 100% de Capacidad</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-mono">
                  Lista
                </span>
              </h5>
              <p className="text-xs text-slate-600 leading-relaxed">
                El contador de 24 horas comenzará a correr en el segundo exacto en que se use la regadera para regar las flores en el jardín.
              </p>
            </div>
          </div>
        )}

        {/* Administrative Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={handleManualWater}
            disabled={isWatering || canWaterStatus.isEmpty}
            className="px-3.5 py-2 rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-800 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-all hover:scale-102 active:scale-98 disabled:opacity-50"
          >
            <Droplets className="w-4 h-4 text-sky-600" />
            <span>Usar regadera (Iniciar conteo de 24h)</span>
          </button>

          <button
            type="button"
            onClick={() => handleSetCanWater(100)}
            className="px-3.5 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-all hover:scale-102 active:scale-98"
          >
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span>Llenar al 100% de inmediato</span>
          </button>

          <button
            type="button"
            onClick={() => handleSetCanWater(0)}
            className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-all hover:scale-102 active:scale-98"
          >
            <RotateCcw className="w-4 h-4 text-slate-500" />
            <span>Vaciar a 0% (Iniciar 24h desde cero)</span>
          </button>
        </div>
      </div>

      {/* SUCCESS BANNER */}
      {successMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs font-bold flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}
    </div>
  );

  if (!showCardWrapper) {
    return content;
  }

  return (
    <div className="bg-white p-5 sm:p-7 rounded-2xl border border-slate-200 shadow-xs max-w-3xl">
      {content}
    </div>
  );
};
